import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { getLangDefinition } from '../../../utils/helpers';
import {
  AgencyContactAddress,
  AgencyContactAddressText,
  AgencyContactsContainer,
  AgencyContactTimetable
} from './styled';
import useScreenSize from '../../../hooks/useScreenSize';
import { PhoneButtonClickHandler } from '../../Ad/BottomPhoneButton/types';
import { useClient } from '../../../hooks/useClient';
import { PlaceholderBlock } from '../../../styled/placeholders.styled';

interface AgencyContactsSectionProps {
  address: string;
  lat?: number | null;
  lng?: number | null;
  timetable?: string | null;
  phonesMasked: string[];
  phonesShown?: string[] | null;
  isVisible: boolean;
  setIsVisible: (v: boolean) => void;
  clickHandler: PhoneButtonClickHandler;
}

const PhonesSection = dynamic(
  () =>
    import('../../Ad/Sections/PhonesSection').then(mod => mod.PhonesSection),
  {
    ssr: false
  }
);

const BottomPhoneButton = dynamic(
  () => import('../../Ad/BottomPhoneButton').then(mod => mod.BottomPhoneButton),
  {
    ssr: false,
    loading: () => <PlaceholderBlock height="60px" margin="0" />
  }
);

export const AgencyContactsSection: React.FC<AgencyContactsSectionProps> = memo(
  ({
    address,
    lat,
    lng,
    timetable,
    phonesMasked,
    phonesShown,
    isVisible,
    setIsVisible,
    clickHandler
  }) => {
    const {
      i18n: { language }
    } = useTranslation();
    const { isSmallOrLargeDesktop } = useScreenSize();
    const isClient = useClient();

    const localeLanguage = getLangDefinition(language);
    const hasCoordinates = typeof lat === 'number' && typeof lng === 'number';

    return (
      <AgencyContactsContainer data-cy="agency-section--contacts">
        {hasCoordinates && address && (
          <AgencyContactAddress
            href={`https://maps.google.com/?q=${lat},${lng}&z=15&lan=${localeLanguage}`}
            target="_blank"
            rel="noreferrer"
            data-cy="contact-item--address"
            data-stat="agency-map"
          >
            {address}
          </AgencyContactAddress>
        )}
        {!hasCoordinates && address && (
          <AgencyContactAddressText data-cy="contact-item--address">
            {address}
          </AgencyContactAddressText>
        )}
        <AgencyContactTimetable data-cy="contact-item--timetable">
          {timetable}
        </AgencyContactTimetable>

        <div>
          {isSmallOrLargeDesktop || !isClient ? (
            <BottomPhoneButton
              phones={phonesShown}
              clickHandler={clickHandler}
              stat="agency-phones-button"
              {...{ setIsVisible, isVisible }}
            />
          ) : (
            <PhonesSection
              phonesMasked={phonesMasked}
              onClickHandler={clickHandler}
              phonesShown={phonesShown}
              stat="agency-contact-box-phone"
            />
          )}
        </div>
      </AgencyContactsContainer>
    );
  }
);

AgencyContactsSection.displayName = 'AgencyContactsSection';
